import { Box, IconButton, Paper } from "@mui/material";
import { Helmet } from "react-helmet";
import StyledMainButton from "components/buttons/StyledMainButton";
import SectionHeader from "shared/SectionHeader";
import { useContext, useMemo, useState } from "react";
import DataTable from "components/DataTable/DataTable";
import { customStyles } from "utils/utils";
import { DeleteForeverRounded } from "@mui/icons-material";
import { useActionWithPath, useSignupPermittedValues } from "utils/query";
import { Message } from "components/SnackbarMessage";
import AlertDialog from "components/AlertDialog";
import PermitDomainDialog from "components/dialogs/PermitDomainDialog";
import LinearProgress from "components/LinearProgress";
import { MessageContext } from "utils/MessageProvider";

interface PermittedDomain {
  domain: string;
}

const PermittedDomains = () => {
  const {
    data: signupPermittedValue,
    isFetching: signupPermittedValueFetching,
    refetch: refetchSignupPermittedValue,
  } = useSignupPermittedValues();

  const [openPermitDialog, setOpenPermitDialog] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState<PermittedDomain | null>(
    null
  );
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const { setMessage } = useContext(MessageContext);

  const showSuccess = (message: string) => {
    setMessage(new Message(message, "success"));
    setTimeout(() => setMessage(null), 10000);
  };

  const showError = (message: string) => {
    setMessage(new Message(message, "error"));
    setTimeout(() => setMessage(null), 10000);
  };

  const permitDomainAction = useActionWithPath({});

  const removePermittedDomainAction = useActionWithPath({
    onSuccess: () => {
      refetchSignupPermittedValue();
    },
  });

  const togglePermittedDialog = () => {
    setOpenPermitDialog(!openPermitDialog);
  };

  const handleAdd = async (domains: PermittedDomain[]) => {
    setOpenPermitDialog(false);

    try {
      await Promise.all(
        domains.map((x) => {
          // @ts-ignore
          return permitDomainAction.mutateAsync({
            method: "post",
            path: `/permitDomain?domain=${x.domain}`,
            useLocalMessage: true,
          });
        })
      );

      if (domains.length === 1) {
        showSuccess(`Successfully permitted ${domains[0].domain}`);
      } else {
        showSuccess(`Successfully permitted domains`);
      }
    } catch (err: any) {
      console.error(err);
      try {
        const json = await err.json();
        if (json) {
          showError(`Error invoking request: ${JSON.stringify(json.message)}`);
        }
      } catch (e) {
        console.log("ERROR:", e);
        showError(`Error invoking request: ${JSON.stringify(err)}`);
      }
    } finally {
      refetchSignupPermittedValue();
    }
  };

  const permittedDomains = useMemo(() => {
    const result: any[] = [];

    if (signupPermittedValue?.DOMAIN?.length) {
      signupPermittedValue.DOMAIN.forEach((domain: string) =>
        result.push({ domain })
      );
    }

    return result;
  }, [signupPermittedValue]);

  return (
    <>
      <Helmet>
        <title>Signup Permitted Domains</title>
      </Helmet>

      <SectionHeader
        title={`Signup Permitted Domains`}
        actions={
          <StyledMainButton
            size="small"
            color={"inherit"}
            style={{
              width: 250,
              fontSize: 14,
            }}
            onClick={togglePermittedDialog}
          >
            Add Domain To Permitted
          </StyledMainButton>
        }
        _deprecate_marginTop={0}
        style={{ padding: "20px 0" }}
      />

      {openPermitDialog && (
        <PermitDomainDialog
          open={openPermitDialog}
          progressPending={permitDomainAction.isLoading}
          handleAdd={handleAdd}
          handleClose={togglePermittedDialog}
        />
      )}

      <AlertDialog
        textTitle={"Removing Confirmation"}
        textContent={
          <>
            Are you sure you want to remove&nbsp;
            <strong className="danger-text">{selectedDomain?.domain}</strong>?
          </>
        }
        open={openConfirmation}
        setOpen={() => setOpenConfirmation(false)}
        handleConfirm={() => {
          // @ts-ignore
          removePermittedDomainAction.mutate({
            method: "delete",
            path: `/removePermittedDomain?domain=${selectedDomain?.domain}`,
            errorMessage: "Error removing permitted domain:",
            successMessage: "Successfully removed permitted domain.",
          });
          setOpenConfirmation(false);
        }}
        isInputConfirmation
        valueToBeDeleted={selectedDomain?.domain}
      />

      {signupPermittedValueFetching && <LinearProgress />}

      <Paper elevation={1}>
        <DataTable
          customStyles={customStyles}
          progressPending={signupPermittedValueFetching}
          pagination={false}
          noDataComponent={
            <Box padding={5} fontWeight={600}>
              No users found
            </Box>
          }
          data={permittedDomains}
          columns={[
            {
              name: "domain",
              label: "Domain",
              selector: (row: PermittedDomain) => row.domain,
              grow: 1,
            },
            {
              name: "actions",
              label: "Actions",
              selector: (row: PermittedDomain) => null,
              right: true,
              renderer(val: any, row: any) {
                return (
                  <IconButton
                    title={"Remove from organization"}
                    onClick={(ev) => {
                      setOpenConfirmation(true);
                      setSelectedDomain(row);
                    }}
                  >
                    <DeleteForeverRounded />
                  </IconButton>
                );
              },
            },
          ]}
        />
      </Paper>
    </>
  );
};

export default PermittedDomains;
